import { useState, useEffect } from 'react'
import { roleLabel } from '../utils/roles'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { initiateSSOLogin } from '../services/api'
import { errorText } from '../utils/errorText'
import { isMicrosoftIdP } from '../utils/sso'
import MicrosoftSignInButton from '../components/MicrosoftSignInButton'
import { DEFAULT_PASSWORD_POLICY, PasswordPolicy, passwordRefusal, unmetRules } from '../utils/passwordPolicy'
import PasswordRules from '../components/PasswordRules'

interface InvitationInfo {
  id: string
  email: string
  role: string
  status: string
  expires_at: string | null
  is_expired: boolean
  tenant: {
    id: string
    name: string
    display_name: string
  } | null
  invited_by: {
    username: string
    email: string
  } | null
  message: string | null
  // 'password': this page takes a password. 'sso': this page starts the tenant's SSO for the
  // invited address and that sign-in accepts the invitation; `sso_available` is false when the
  // tenant's SSO no longer routes the address, and there is no password to fall back to.
  sign_in_method: 'password' | 'sso'
  sso_available: boolean
  sso_display_name: string | null
  sso_mode: string | null
  idp_hint: string | null
  /** The inviting organization's password rules; shown before the password is typed. */
  password_policy?: PasswordPolicy | null
}

export default function AcceptInvitation() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ssoLoading, setSSOLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const fetchInvitationInfo = async () => {
      if (!token) {
        setError('No invitation token provided')
        setIsLoading(false)
        return
      }

      try {
        const response = await fetch(`/api/users/invitations/info/${token}`)
        if (!response.ok) {
          if (response.status === 404) {
            setError('This invitation link is invalid or has already been used.')
          } else {
            setError('Failed to load invitation details.')
          }
          setIsLoading(false)
          return
        }
        const data = await response.json()

        if (data.is_expired) {
          setError('This invitation has expired. Please ask your administrator to send a new invitation.')
          setIsLoading(false)
          return
        }

        if (data.status !== 'pending') {
          setError('This invitation has already been accepted or revoked.')
          setIsLoading(false)
          return
        }

        setInvitationInfo(data)
      } catch {
        setError('Failed to connect to the server. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchInvitationInfo()
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!fullName.trim() || fullName.trim().length < 2) {
      setError('Please enter your full name (at least 2 characters)')
      return
    }

    const unmet = unmetRules(password, invitationInfo?.password_policy || DEFAULT_PASSWORD_POLICY)
    if (unmet.length) {
      setError(passwordRefusal(unmet))
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await fetch('/api/users/invitations/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          full_name: fullName.trim(),
          password,
        }),
      })

      if (!response.ok) {
        let errorMessage = 'Failed to accept invitation'
        try {
          const data = await response.json()
          if (typeof data.detail === 'string') {
            errorMessage = data.detail
          } else if (Array.isArray(data.detail) && data.detail.length > 0) {
            errorMessage = data.detail.map((e: { msg?: string }) => e.msg).filter(Boolean).join('; ') || errorMessage
          }
        } catch {
          errorMessage = `Server error (${response.status})`
        }
        setError(errorMessage)
        return
      }

      setSuccess(true)
    } catch {
      setError('Failed to connect to the server. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Sign-in through the tenant's IdP for the invited address; the callback redeems the
  // invitation and lands the user in the portal, so this page is left for good.
  const handleSSOSignIn = async () => {
    if (!invitationInfo) return
    setError(null)
    setSSOLoading(true)
    try {
      const result = await initiateSSOLogin(invitationInfo.email)
      window.location.href = result.auth_url
    } catch (err: any) {
      setError(errorText(err, 'Failed to start single sign-on'))
      setSSOLoading(false)
    }
  }

  const formatRole = (role: string) => roleLabel(role)

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-theme-text-secondary">Loading invitation...</span>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
            <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
          </div>

          <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                Account created successfully!
              </p>
            </div>
            <p className="text-sm text-green-600 dark:text-green-400 mt-2">
              You can now sign in with your email and password.
            </p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    )
  }

  if (!invitationInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
            <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
          </div>

          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="w-full py-2 px-4 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
      <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
        <div className="flex items-center justify-center space-x-3 mb-6">
          <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
          <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-theme-text-primary mb-2">Accept Invitation</h3>
          <div className="text-sm text-theme-text-secondary space-y-1">
            {invitationInfo.tenant && (
              <p>
                You've been invited to join <span className="font-medium text-theme-text-primary">{invitationInfo.tenant.display_name || invitationInfo.tenant.name}</span>
              </p>
            )}
            <p>
              Role: <span className="font-medium text-theme-text-primary">{formatRole(invitationInfo.role)}</span>
            </p>
            <p>
              Email: <span className="font-medium text-theme-text-primary">{invitationInfo.email}</span>
            </p>
            {invitationInfo.invited_by && (
              <p>
                Invited by: <span className="font-medium text-theme-text-primary">{invitationInfo.invited_by.username}</span>
              </p>
            )}
          </div>
          {invitationInfo.message && (
            <div className="mt-3 p-3 bg-theme-secondary rounded-lg text-sm text-theme-text-secondary italic">
              "{invitationInfo.message}"
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {invitationInfo.sign_in_method === 'sso' && invitationInfo.sso_available && (
          <div className="space-y-3" data-testid="sso-sign-in">
            {isMicrosoftIdP(invitationInfo) ? (
              <div className="flex justify-center">
                <MicrosoftSignInButton onClick={handleSSOSignIn} disabled={ssoLoading} />
              </div>
            ) : (
              <button
                type="button"
                onClick={handleSSOSignIn}
                disabled={ssoLoading}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {ssoLoading ? 'Redirecting...' : `Continue with ${invitationInfo.sso_display_name || 'single sign-on'}`}
              </button>
            )}
            <p className="text-xs text-center text-theme-text-secondary">
              {`Sign in with ${isMicrosoftIdP(invitationInfo) ? 'your work or school account' : "your organization's account"} (${invitationInfo.email}) to accept this invitation.`}
            </p>
          </div>
        )}

        {invitationInfo.sign_in_method === 'sso' && !invitationInfo.sso_available && (
          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-md" data-testid="sso-unavailable">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              This invitation is accepted by signing in with single sign-on, which is not currently enabled
              for {invitationInfo.email}. Contact your administrator.
            </p>
          </div>
        )}

        {invitationInfo.sign_in_method === 'password' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                Full Name
              </label>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter your full name"
                minLength={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="Create a password"
                autoComplete="new-password"
              />
              <PasswordRules
                password={password}
                policy={invitationInfo.password_policy || DEFAULT_PASSWORD_POLICY}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="Confirm your password"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Creating account...' : 'Accept & Create Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
